import { Grid, Message } from '@mairistem/composants';
import {
	fetchUserServicesWithUserRole,
	getErrorMessage,
	Icon,
	UserRole,
	useEtiquette,
	useUserRole,
} from '@mairistem/finances-utils';
import dayjs from 'dayjs';
import { deburr } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import StatutMontantSimulation from '../../../../constants/simulation/statutMontantSimulation';
import TypePeriode from '../../../../constants/simulation/typePeriode';
import type TreeRow from '../../../../interfaces/feuilleSaisie/treeRow';
import type IdeeImportable from '../../../../interfaces/simulation/ideeImportable';
import type {
	default as MontantSimulation,
	MontantSimulationUpdate,
} from '../../../../interfaces/simulation/montantSimulation';
import { getServices as getServicesImputationSimulation } from '../../../../services/simulation/imputationSimulation';
import {
	deleteMontant,
	importerIdees,
	updateMontant,
	upsertDemandeMontant,
	upsertMontant,
} from '../../../../services/simulation/montantSimulation';
import { getService } from '../../../../services/simulation/service';
import { getMontantColumnFromPeriode } from '../../../../utils/feuilleSaisie/imputation';
import { getImputationLigne } from '../../../../utils/imputation';
import { isMontantSansOrigine } from '../../../../utils/montantSimulation';
import {
	getToastsImport,
	type ToastImport,
} from '../../../../utils/simulation/toastsImportIdees';
import {
	getNomUtilisateur,
	LOGIN_NON_RENSEIGNE,
} from '../../../../utils/simulation/utilisateur';
import ImportIdeeModal from './ImportIdeeModal/ImportIdeeModal';
import './MontantsSimulationModalContent.css';
import MontantsSimulationModalContentHeader from './MontantsSimulationModalContentHeader';
import MontantsSimulationModalContentItem, {
	type Service,
} from './MontantsSimulationModalContentItem';

const STATUT_LABELS: Record<string, string> = {
	[StatutMontantSimulation.A_ARBITRER]: 'À arbitrer',
	[StatutMontantSimulation.ACCEPTE]: 'Accepté',
	[StatutMontantSimulation.REFUSE]: 'Refusé',
};

// -1 (et non 0) pour ne pas etre confondu avec un identifiantSvutCpta non
// renseigne (0/null/undefined), ce qui permet de distinguer un montant sans
// service explicitement choisi comme tel d'un montant pas encore rattache
const AUCUN_SERVICE_IDENTIFIANT = -1;

export const NO_SERVICE_OPTION: Service = {
	'data-testid': 'serviceAucun',
	key: AUCUN_SERVICE_IDENTIFIANT,
	text: 'Aucun service',
	value: AUCUN_SERVICE_IDENTIFIANT,
};

// Le type Service de finances-utils ne declare que identifiant/code/libelle,
// mais la route userServicesWithUserRole renvoie egalement, pour chaque
// service, le role de l'utilisateur courant sur ce service precis
interface ServiceAvecRoleUtilisateur {
	identifiant: number;
	libelle: string;
	userRole?: { useGestionnaire: boolean };
}

// Reponse (partielle) de la route imputationSimulation/services : identifiant
// sert a l'appartenance a l'imputation, libelle a proposer a un Admin les
// services auxquels il n'est pas lui-meme rattache
interface ServiceRattacheImputation {
	identifiant: number;
	libelle: string;
}

const normaliser = (valeur: string) => deburr(valeur.toLowerCase());

const matchesSearch = (
	montantSimulation: MontantSimulation,
	search: string,
	nomUtilisateur: string,
	libelleService: string,
	isCA: boolean,
) => {
	if (!search) return true;

	const statut = montantSimulation.statut;
	const dateCreation = montantSimulation.dateCreation
		? dayjs(montantSimulation.dateCreation).format('DD/MM/YYYY')
		: '';

	// Au CA, seul le reste a realiser est affiche (rep/demande/prop/vote et le
	// statut sont masques) : la recherche ne doit porter que sur ce qui est
	// effectivement visible
	const montants = isCA
		? [montantSimulation.rar]
		: [
				montantSimulation.rep,
				montantSimulation.demande,
				montantSimulation.prop,
				montantSimulation.vote,
			];

	return (
		normaliser(libelleService).includes(search) ||
		(montantSimulation.etiquettes ?? []).some((etiquette) =>
			normaliser(etiquette.libelle ?? '').includes(search),
		) ||
		normaliser(montantSimulation.commentaire ?? '').includes(search) ||
		normaliser(montantSimulation.type ?? '').includes(search) ||
		(!isCA && normaliser(STATUT_LABELS[statut] ?? statut).includes(search)) ||
		normaliser(nomUtilisateur).includes(search) ||
		dateCreation.includes(search) ||
		montants.some((montant) => String(montant ?? '').includes(search))
	);
};

// Toasts de l'import d'idees (en haut a droite, empiles, fermes apres 8 s) :
// picto d'information en couleur metier, ou en couleur danger pour un echec.
// Contenu dans une div : react-toastify 11 le pose directement dans le toast
// (flex), un fragment mettrait titre et texte en colonnes cote a cote
const afficherToastImport = ({ danger, texte, titre }: ToastImport) =>
	toast.info(
		<div>
			<strong>{titre}</strong>
			<br />
			{texte}
		</div>,
		{
			autoClose: 8000,
			icon: (
				<Icon
					color={danger ? 'red' : 'var(--accent)'}
					iconSet="Lucide"
					name="Info"
					size={18}
				/>
			),
		},
	);

interface MontantsSimulationModalContentProps {
	anneeExercice?: number;
	identifiantEntite: number;
	identifiantExercice: number;
	identifiantSimulation: number;
	isDemandeEnabled: boolean;
	libelleBudget?: string;
	montantsSimulation: MontantSimulation[];
	// Incremente le nombre de montants de la ligne dans l'arbre (1 par defaut)
	onAddMontant: (nombre?: number) => void;
	onChangeCommentaire: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChangeMontant: (
		differenceMontant: number,
		typePeriode: TypePeriode,
		nameColumn: string,
	) => void;
	onRemoveMontant: () => void;
	rowData: TreeRow;
	saisieBudgetService: boolean;
	setMontantsSimulation: React.Dispatch<
		React.SetStateAction<MontantSimulation[]>
	>;
	showRar: boolean;
	// Signale a la modal une sauvegarde en cours, a attendre avant de relire
	// les montants a la fermeture
	suivreSauvegarde: (sauvegarde: Promise<void>) => void;
	transfertCpta: boolean;
	typePeriode: TypePeriode;
	userRole: UserRole;
}

const MontantsSimulationModalContent = ({
	anneeExercice,
	identifiantEntite,
	identifiantExercice,
	identifiantSimulation,
	isDemandeEnabled,
	libelleBudget,
	montantsSimulation,
	onAddMontant,
	onChangeCommentaire,
	onChangeMontant,
	onRemoveMontant,
	rowData,
	saisieBudgetService,
	setMontantsSimulation,
	showRar,
	suivreSauvegarde,
	transfertCpta,
	typePeriode,
	userRole,
}: MontantsSimulationModalContentProps) => {
	const [loadingCreation, setLoadingCreation] = React.useState(false);
	const [search, setSearch] = React.useState('');
	const [importOpen, setImportOpen] = React.useState(false);
	const [noms, setNoms] = React.useState<Record<number, string>>({});
	const [services, setServices] = React.useState<Service[]>([]);
	const [servicesExternes, setServicesExternes] = React.useState<Service[]>([]);
	const [servicesLoading, setServicesLoading] = React.useState(true);
	// Services effectivement rattaches a chaque ImputationSimulation, par
	// identifiantImputationSimulation (undefined tant que non charge)
	const [servicesParImputation, setServicesParImputation] = React.useState<
		Record<number, ServiceRattacheImputation[]>
	>({});
	// Cache des requetes en cours/terminees, pour ne jamais interroger deux fois
	// la meme ImputationSimulation (partage entre l'effet ci-dessous et les
	// choix de service par defaut a la creation/duplication)
	const servicesImputationCache = React.useRef<
		Map<number, Promise<ServiceRattacheImputation[]>>
	>(new Map());
	const listRef = React.useRef<HTMLDivElement>(null);
	const [, userServiceUtilisateur] = useUserRole();

	const showServiceCombobox = saisieBudgetService;

	// Sans gestion des demandes (et hors gestion par service), la modal ne sert
	// qu'a consulter les montants de la ligne : on ne peut y creer (ajout ou
	// duplication) qu'une seule ligne sans origine. En gestion par service, la
	// creation de lignes n'est pas limitee, meme sans le module des demandes
	const creationLigneImpossible =
		!saisieBudgetService &&
		!isDemandeEnabled &&
		montantsSimulation.some(isMontantSansOrigine);

	React.useEffect(() => {
		const loadServices = async () => {
			try {
				const reponse =
					await fetchUserServicesWithUserRole(identifiantExercice);
				// fetchUserServicesWithUserRole met sa reponse en cache dans le
				// sessionStorage (cf @mairistem/finances-utils) : une entree y ayant
				// ete ecrite sous une forme non deballee (ex: { data: [...] } au lieu
				// de [...], par une version anterieure de la lib) resterait servie
				// telle quelle indefiniment. On se protege donc ici plutot que de
				// faire confiance au typage.
				const fetchedServices: ServiceAvecRoleUtilisateur[] = Array.isArray(
					reponse,
				)
					? reponse
					: ((reponse as unknown as { data?: ServiceAvecRoleUtilisateur[] })
							?.data ?? []);
				// Un Gestionnaire ne peut choisir/agir que sur les services dont il est
				// effectivement gestionnaire, pas sur ceux auxquels il est seulement
				// rattache (ex: en tant que simple membre d'un autre service)
				const servicesUtilisables =
					userRole === UserRole.GESTIONNAIRE
						? fetchedServices.filter(
								(service) =>
									(service as unknown as ServiceAvecRoleUtilisateur).userRole
										?.useGestionnaire,
							)
						: fetchedServices;
				// Un Gestionnaire est necessairement rattache a un service : l'option
				// "Aucun service" ne lui est pas proposee
				setServices([
					...(userRole === UserRole.GESTIONNAIRE ? [] : [NO_SERVICE_OPTION]),
					...servicesUtilisables.map((service) => ({
						'data-testid': `service${service.libelle}`,
						key: service.identifiant,
						text: service.libelle,
						value: service.identifiant,
					})),
				]);
			} catch (e) {
				toast.error(
					getErrorMessage(e, 'Erreur lors de la récupération des services'),
				);
			} finally {
				setServicesLoading(false);
			}
		};

		loadServices();
	}, [identifiantExercice, userRole]);

	// Recupere (avec cache) les services effectivement rattaches a une
	// ImputationSimulation, pour restreindre les services proposables sur ses
	// montants a l'intersection avec les services de l'utilisateur
	const fetchServicesRattaches = React.useCallback(
		(identifiantImputationSimulation: number) => {
			if (
				!servicesImputationCache.current.has(identifiantImputationSimulation)
			) {
				// La requete (HttpRequest de @mairistem/tools) n'est pas une vraie
				// Promise : son .then() ne peut etre consomme qu'une seule fois (les
				// abonnements ajoutes apres coup, ici via le cache, ne sont jamais
				// rappeles). On la convertit donc immediatement en Promise native
				// avec Promise.resolve pour pouvoir la mettre en cache et l'attendre
				// depuis plusieurs endroits sans qu'elle reste bloquee indefiniment.
				servicesImputationCache.current.set(
					identifiantImputationSimulation,
					Promise.resolve(
						getServicesImputationSimulation(identifiantImputationSimulation),
					).catch(() => []),
				);
			}

			return servicesImputationCache.current.get(
				identifiantImputationSimulation,
			) as Promise<ServiceRattacheImputation[]>;
		},
		[],
	);

	// Tous les montants de la modal portent la meme ImputationSimulation (la
	// modal est ouverte pour une seule ligne budgetaire) : un seul identifiant
	// est a resoudre, pas un par montant
	React.useEffect(() => {
		const identifiantImputation =
			montantsSimulation[0]?.identifiantImputationSimulation;
		if (
			!identifiantImputation ||
			identifiantImputation in servicesParImputation
		) {
			return;
		}

		fetchServicesRattaches(identifiantImputation).then((servicesRattaches) => {
			setServicesParImputation((old) =>
				identifiantImputation in old
					? old
					: { ...old, [identifiantImputation]: servicesRattaches },
			);
		});
	}, [montantsSimulation, servicesParImputation, fetchServicesRattaches]);

	// Un montant peut porter un service qui n'appartient pas aux services de
	// l'utilisateur courant (donc absent de `services`) : on va quand meme
	// chercher son libelle pour pouvoir l'afficher (en lecture seule). On
	// attend que `services` soit charge pour ne pas interroger des services
	// qui vont de toute facon y apparaitre.
	React.useEffect(() => {
		if (servicesLoading) return;

		const identifiantsConnus = new Set([
			...services.map((service) => service.value),
			...servicesExternes.map((service) => service.value),
		]);

		const identifiantsManquants = new Set(
			montantsSimulation
				.map((montant) => montant.identifiantSvutCpta)
				.filter(
					(identifiant): identifiant is number =>
						Boolean(identifiant) &&
						identifiant !== AUCUN_SERVICE_IDENTIFIANT &&
						!identifiantsConnus.has(identifiant),
				),
		);

		identifiantsManquants.forEach((identifiant) => {
			getService(identifiant)
				.then((service) => {
					setServicesExternes((old) =>
						old.some((serviceExterne) => serviceExterne.value === identifiant)
							? old
							: [
									...old,
									{
										'data-testid': `service${service.libelle}`,
										key: service.identifiant,
										text: service.libelle,
										value: service.identifiant,
									},
								],
					);
				})
				.catch(() => {});
		});
	}, [montantsSimulation, services, servicesExternes, servicesLoading]);

	const identifiantsServicesUtilisateur = React.useMemo(
		() => services.map((service) => service.value),
		[services],
	);

	// En gestion par service, un Gestionnaire ne peut ni ajouter ni importer de
	// demande sur une ligne a laquelle aucun de ses services n'est rattache : la
	// demande serait creee sans service et il ne pourrait plus rien y saisir.
	// Les services rattaches ne sont connus qu'a partir d'un montant de la ligne
	// (la ligne de l'arbre ne porte pas son ImputationSimulation)
	const servicesRattachesLigne =
		servicesParImputation[
			montantsSimulation[0]?.identifiantImputationSimulation
		];
	const aucunServiceRattache =
		saisieBudgetService &&
		userRole < UserRole.CHARGE_PILOTAGE &&
		servicesRattachesLigne !== undefined &&
		!servicesRattachesLigne.some((service) =>
			identifiantsServicesUtilisateur.includes(Number(service.identifiant)),
		);

	// Un Admin peut choisir n'importe quel service rattache a l'imputation, y
	// compris ceux auxquels il n'est pas lui-meme rattache : on complete donc
	// ses services avec ceux de l'imputation qui n'y figurent pas encore
	const completerServicesAdmin = (
		servicesRattaches: ServiceRattacheImputation[],
	) => {
		if (userRole !== UserRole.ADMIN) return services;

		const identifiantsConnus = new Set(
			services.map((service) => service.value),
		);

		return [
			...services,
			...servicesRattaches
				.filter(
					(service) => !identifiantsConnus.has(Number(service.identifiant)),
				)
				.map((service) => ({
					'data-testid': `service${service.libelle}`,
					key: Number(service.identifiant),
					text: service.libelle,
					value: Number(service.identifiant),
				})),
		];
	};

	// Resolution des noms/prenoms des utilisateurs pour la recherche (mis en cache par identifiantLogin)
	React.useEffect(() => {
		const identifiantsManquants = new Set(
			montantsSimulation
				.map((montant) => montant.identifiantLogin)
				.filter(
					(identifiant): identifiant is number =>
						Boolean(identifiant) && !(identifiant in noms),
				),
		);

		identifiantsManquants.forEach((identifiant) => {
			getNomUtilisateur(identifiant).then((nom) => {
				setNoms((old) =>
					identifiant in old ? old : { ...old, [identifiant]: nom },
				);
			});
		});
	}, [montantsSimulation, noms]);

	// La nouvelle demande est epinglee en haut de la liste, on repositionne le scroll en haut pour qu'elle soit visible
	const scrollListToTop = () => {
		if (listRef.current) {
			listRef.current.scrollTop = 0;
		}
	};

	const EtiquettesInput = useEtiquette(
		'/api/finances/simulation/etiquettes',
		identifiantEntite,
	);

	const handleUpsertMontant = async () => {
		const data = {
			...getImputationLigne(identifiantSimulation, rowData),
			onlyCreate: true,
		};

		let montantSimulation = {} as MontantSimulation;

		// Si on est en demande, on fait une requete sur une autre route accessible par les gestionnaires
		if (typePeriode === TypePeriode.DEMANDE) {
			montantSimulation = await upsertDemandeMontant(data);
		} else {
			montantSimulation = await upsertMontant(data);
		}

		return montantSimulation;
	};

	// Restreint les services de l'utilisateur a ceux effectivement rattaches a
	// l'ImputationSimulation donnee. Garde defensive au cas ou la reponse du
	// backend ne porterait pas cet identifiant : on renvoie alors les services
	// de l'utilisateur tels quels plutot que de tout filtrer.
	const getServicesUtilisablesPourImputation = async (
		identifiantImputationSimulation?: number,
	) => {
		if (!identifiantImputationSimulation) return services;

		const servicesRattaches = await fetchServicesRattaches(
			identifiantImputationSimulation,
		);
		// La route renvoie parfois identifiant en string (ex: "9") alors que
		// services (userServicesWithUserRole) le renvoie en number : on normalise
		// pour que la comparaison ci-dessous fonctionne dans tous les cas
		const identifiantsRattaches = new Set(
			servicesRattaches.map((service) => Number(service.identifiant)),
		);

		// "Aucun service" n'est pas un vrai service rattachable, mais doit
		// rester disponible (ADMIN/CHARGE_PILOTAGE ne sont pas rattaches a un
		// service unique et peuvent laisser une demande sans service)
		return completerServicesAdmin(servicesRattaches).filter(
			(service) =>
				service.value === AUCUN_SERVICE_IDENTIFIANT ||
				identifiantsRattaches.has(service.value),
		);
	};

	// La combobox de service affiche par defaut le premier service de la liste
	// tant qu'aucun n'est choisi (cf MontantsSimulationModalContentItem) ; on
	// enregistre ce choix par defaut des la creation pour que le montant soit
	// reellement rattache a ce service (et non plus seulement affiche comme tel)
	const appliquerServiceParDefaut = async (
		montantSimulation: MontantSimulation,
	) => {
		const servicesUtilisables = await getServicesUtilisablesPourImputation(
			montantSimulation.identifiantImputationSimulation,
		);
		const identifiantSvutCptaParDefaut = servicesUtilisables[0]?.value;
		if (!identifiantSvutCptaParDefaut) return montantSimulation;

		return updateMontant({
			identifiant: montantSimulation.identifiant,
			identifiantSvutCpta: identifiantSvutCptaParDefaut,
		});
	};

	// En periode de vote ou de proposition, une demande nouvellement ajoutee
	// est directement consideree comme acceptee (elle n'a plus a passer par
	// l'arbitrage) : seuls Admin/ChargePilotage peuvent ajouter sur ces
	// periodes (cf isInputDisabled), l'ajout vaut donc deja arbitrage
	const appliquerStatutParDefaut = async (
		montantSimulation: MontantSimulation,
	) => {
		const doitAccepterDirectement =
			typePeriode === TypePeriode.VOTE || typePeriode === TypePeriode.PROPOSE;

		if (!doitAccepterDirectement) return montantSimulation;

		return updateMontant({
			identifiant: montantSimulation.identifiant,
			statut: StatutMontantSimulation.ACCEPTE,
		});
	};

	// Enveloppe une action qui enregistre en base pour la signaler a la modal
	const avecSuiviSauvegarde =
		<A extends unknown[]>(action: (...args: A) => Promise<void>) =>
		(...args: A) => {
			const sauvegarde = action(...args);
			suivreSauvegarde(sauvegarde);
			return sauvegarde;
		};

	const handleAddMontant = avecSuiviSauvegarde(async () => {
		try {
			setLoadingCreation(true);
			let montantSimulation = await handleUpsertMontant();
			montantSimulation = await appliquerServiceParDefaut(montantSimulation);
			montantSimulation = await appliquerStatutParDefaut(montantSimulation);
			setMontantsSimulation((old) => [montantSimulation, ...old]);
			// On incremente le nombre de montant dans la rowData
			onAddMontant();
			scrollListToTop();
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors de la création du montant'));
		} finally {
			setLoadingCreation(false);
		}
	});

	// Une demande acceptee par idee importee, creee et preremplie par le back
	// (valeurs figees a l'import). Comme l'ajout, l'import est enregistre
	// immediatement : les demandes sont inserees en tete et reprendront l'ordre
	// chronologique a la reouverture de la modale. Le back ignore en silence une
	// idee devenue non eligible : il peut y avoir moins de demandes que d'idees.
	// Ses refus de droits (403) sont affiches tels quels par getErrorMessage
	const handleImportIdees = avecSuiviSauvegarde(
		async (idees: IdeeImportable[]) => {
			try {
				const {
					affectations = [],
					data,
					refusees = [],
				} = await importerIdees({
					...getImputationLigne(identifiantSimulation, rowData),
					identifiantsIdees: idees.map((idee) => idee.identifiant),
					typePeriode,
				});
				setMontantsSimulation((old) => [...data, ...old]);
				// Un seul report dans l'arbre pour toutes les demandes creees : les
				// callbacks de l'arbre repartent de son etat courant, des appels
				// successifs dans le meme tour s'ecraseraient (le RAR est nul)
				if (data.length > 0) {
					onAddMontant(data.length);
					const nameColumn = getMontantColumnFromPeriode(typePeriode);
					if (nameColumn) {
						onChangeMontant(
							data.reduce(
								(total, montant) => total + (montant[nameColumn] ?? 0),
								0,
							),
							typePeriode,
							nameColumn,
						);
					}
				}
				scrollListToTop();
				// Ni les idees perdues lors d'un import concurrent, ni le service
				// affecte aux idees sans service ne sont silencieux
				for (const toastImport of getToastsImport({ affectations, refusees })) {
					afficherToastImport(toastImport);
				}
				setImportOpen(false);
			} catch (e) {
				toast.error(getErrorMessage(e, "Erreur lors de l'import des idées"));
			}
		},
	);

	const handleMontantSimulationChange = avecSuiviSauvegarde(
		async (montantSimulation: MontantSimulationUpdate) => {
			try {
				const data = await updateMontant(
					montantSimulation,
					new URLSearchParams({ withEtiquettes: 'true' }),
				);
				setMontantsSimulation((old) =>
					old.map((montant) =>
						montant.identifiant === montantSimulation.identifiant
							? data
							: montant,
					),
				);
			} catch (e) {
				toast.error(
					getErrorMessage(e, 'Erreur lors de la mise à jour du montant'),
				);
			}
		},
	);

	const handleDuplicateMontant = avecSuiviSauvegarde(
		async (montantSimulation: MontantSimulation) => {
			try {
				setLoadingCreation(true);
				const createdMontantSimulation = await handleUpsertMontant();
				const servicesUtilisables = await getServicesUtilisablesPourImputation(
					createdMontantSimulation.identifiantImputationSimulation,
				);
				// On conserve le service du montant duplique s'il reste utilisable par
				// l'utilisateur sur cette imputation, sinon on retombe sur le service
				// par defaut (premier de la liste)
				const identifiantSvutCptaParDefaut = servicesUtilisables.some(
					(service) => service.value === montantSimulation.identifiantSvutCpta,
				)
					? montantSimulation.identifiantSvutCpta
					: servicesUtilisables[0]?.value;
				const duplicatedMontantSimulation = await updateMontant(
					{
						commentaire: montantSimulation.commentaire,
						demande: montantSimulation.demande,
						etiquettes: montantSimulation.etiquettes,
						identifiant: createdMontantSimulation.identifiant,
						...(identifiantSvutCptaParDefaut
							? { identifiantSvutCpta: identifiantSvutCptaParDefaut }
							: {}),
						prop: montantSimulation.prop,
						rar: montantSimulation.rar,
						rep: montantSimulation.rep,
						statut: StatutMontantSimulation.A_ARBITRER,
						vote: montantSimulation.vote,
					},
					new URLSearchParams({ withEtiquettes: 'true' }),
				);
				setMontantsSimulation((old) => [duplicatedMontantSimulation, ...old]);
				// On incremente le nombre de montant dans la rowData
				onAddMontant();
				scrollListToTop();

				// Le RAR est toujours pris en compte dans le total independamment de la periode
				onChangeMontant(
					duplicatedMontantSimulation.rep ?? 0,
					typePeriode,
					'rep',
				);

				const nameColumn = getMontantColumnFromPeriode(typePeriode);
				if (nameColumn) {
					onChangeMontant(
						duplicatedMontantSimulation[nameColumn] ?? 0,
						typePeriode,
						nameColumn,
					);
				}
			} catch (e) {
				toast.error(
					getErrorMessage(e, 'Erreur lors de la duplication du montant'),
				);
			} finally {
				setLoadingCreation(false);
			}
		},
	);

	const handleDeleteMontant = avecSuiviSauvegarde(
		async (montantSimulation: MontantSimulation) => {
			try {
				await deleteMontant(montantSimulation.identifiant, typePeriode);
				setMontantsSimulation((old) =>
					old.filter(
						(montant) => montant.identifiant !== montantSimulation.identifiant,
					),
				);
				// On decremente le nombre de montant dans la rowData
				onRemoveMontant();

				// Le RAR est toujours pris en compte dans le total independamment de la periode
				onChangeMontant(-(montantSimulation.rep ?? 0), typePeriode, 'rep');

				const nameColumn = getMontantColumnFromPeriode(typePeriode);
				// Au CA, le statut (herite d'une periode precedente) n'a plus cours et
				// ne doit donc plus exclure la mise a jour du rar
				if (
					nameColumn &&
					(isCA || montantSimulation.statut !== StatutMontantSimulation.REFUSE)
				) {
					onChangeMontant(
						-(montantSimulation[nameColumn] ?? 0),
						typePeriode,
						nameColumn,
					);
				}
			} catch (e) {
				toast.error(
					getErrorMessage(e, 'Erreur lors de la suppression du montant'),
				);
			}
		},
	);

	// N'ajoute le service externe du montant que pour ce montant precis, pour
	// ne pas exposer aux autres lignes des services d'utilisateurs differents
	const getServicesPourMontant = (montantSimulation: MontantSimulation) => {
		const servicesRattaches =
			servicesParImputation[montantSimulation.identifiantImputationSimulation];
		const servicesProposables = servicesRattaches
			? completerServicesAdmin(servicesRattaches)
			: services;

		const servicesUtilisateur = servicesProposables.some(
			(service) => service.value === montantSimulation.identifiantSvutCpta,
		)
			? servicesProposables
			: (() => {
					// Un Gestionnaire n'a pas "Aucun service" dans sa liste : on
					// l'ajoute pour un montant non rattache comme pour un montant
					// explicitement sans service (-1), sans quoi son libelle serait vide
					if (
						!montantSimulation.identifiantSvutCpta ||
						montantSimulation.identifiantSvutCpta === AUCUN_SERVICE_IDENTIFIANT
					) {
						return servicesProposables.some(
							(service) => service.value === AUCUN_SERVICE_IDENTIFIANT,
						)
							? servicesProposables
							: [NO_SERVICE_OPTION, ...servicesProposables];
					}

					const serviceExterne = servicesExternes.find(
						(service) =>
							service.value === montantSimulation.identifiantSvutCpta,
					);

					return serviceExterne
						? [...servicesProposables, serviceExterne]
						: servicesProposables;
				})();

		// Tant que les services rattaches a l'imputation ne sont pas encore
		// charges, on ne restreint pas encore la liste (pour eviter un "flash" a
		// vide pendant le chargement)
		if (!servicesRattaches) return servicesUtilisateur;

		// La route renvoie parfois identifiant en string (ex: "9") alors que
		// services (userServicesWithUserRole) le renvoie en number : on normalise
		// pour que la comparaison ci-dessous fonctionne dans tous les cas
		const identifiantsRattaches = new Set(
			servicesRattaches.map((service) => Number(service.identifiant)),
		);

		// Le service courant du montant (deja enregistre) reste toujours
		// affichable, meme s'il n'est plus rattache a l'imputation, de meme que
		// "Aucun service" qui n'est pas un vrai service rattachable
		return servicesUtilisateur.filter(
			(service) =>
				service.value === AUCUN_SERVICE_IDENTIFIANT ||
				service.value === montantSimulation.identifiantSvutCpta ||
				identifiantsRattaches.has(service.value),
		);
	};

	const isCA = typePeriode === TypePeriode.CA_CFU;

	const searchNormalise = normaliser(search.trim());
	// montantsSimulation est deja dans l'ordre d'affichage : trie par date de
	// creation a la recuperation initiale, puis les creations/duplications
	// sont systematiquement ajoutees en tete (cf. handleAddMontant et
	// handleDuplicateMontant), donc pas besoin de retrier ici
	const montantsSimulationTries = montantsSimulation.filter(
		(montantSimulation) => {
			const servicesPourMontant = getServicesPourMontant(montantSimulation);
			// Meme logique de repli que l'affichage (texte ou combobox) : tant
			// qu'aucun service n'est choisi, c'est le premier service de la liste
			// qui est effectivement affiche
			const identifiantSvutCptaAffiche =
				montantSimulation.identifiantSvutCpta ||
				(servicesPourMontant[0]?.value ?? 0);

			return matchesSearch(
				montantSimulation,
				searchNormalise,
				noms[montantSimulation.identifiantLogin ?? -1] ?? LOGIN_NON_RENSEIGNE,
				servicesPourMontant.find(
					(service) => service.value === identifiantSvutCptaAffiche,
				)?.text ?? '',
				isCA,
			);
		},
	);

	return (
		<>
			<Grid className="montantsSimulationModalContentHeader">
				<MontantsSimulationModalContentHeader
					aucunServiceRattache={aucunServiceRattache}
					creationLigneImpossible={creationLigneImpossible}
					isDemandeEnabled={isDemandeEnabled}
					loading={loadingCreation}
					montantsSimulation={montantsSimulationTries}
					onAddMontant={handleAddMontant}
					onOpenImport={() => setImportOpen(true)}
					onSearchChange={setSearch}
					search={search}
					transfertCpta={transfertCpta}
					typePeriode={typePeriode}
					userRole={userRole}
				/>
			</Grid>
			<div className="montantsSimulationModalContentList" ref={listRef}>
				{searchNormalise && montantsSimulationTries.length === 0 ? (
					<Message
						content="Aucune demande ne correspond à cette recherche"
						data-testid="montantsSimulationSearchEmpty"
						size="tiny"
					/>
				) : (
					montantsSimulationTries.map((montantSimulation) => (
						<MontantsSimulationModalContentItem
							creationLigneImpossible={creationLigneImpossible}
							EtiquettesInput={EtiquettesInput}
							identifiantLoginUtilisateur={
								userServiceUtilisateur?.identifiantLogin
							}
							key={montantSimulation.identifiant}
							montantSimulation={montantSimulation}
							onChange={(data: MontantSimulationUpdate) =>
								handleMontantSimulationChange({
									...data,
									identifiant: montantSimulation.identifiant,
								})
							}
							identifiantsServicesUtilisateur={identifiantsServicesUtilisateur}
							onChangeCommentaire={onChangeCommentaire}
							onChangeMontant={onChangeMontant}
							onDelete={handleDeleteMontant}
							onDuplicate={handleDuplicateMontant}
							services={getServicesPourMontant(montantSimulation)}
							servicesLoading={servicesLoading}
							showRar={showRar}
							showServiceCombobox={showServiceCombobox}
							transfertCpta={transfertCpta}
							typePeriode={typePeriode}
							userRole={userRole}
						/>
					))
				)}
			</div>
			{importOpen && (
				<ImportIdeeModal
					anneeExercice={anneeExercice}
					identifiantSimulation={identifiantSimulation}
					libelleBudget={libelleBudget}
					onClose={() => setImportOpen(false)}
					onImport={handleImportIdees}
					rowData={rowData}
					saisieBudgetService={saisieBudgetService}
					userRole={userRole}
				/>
			)}
		</>
	);
};

export default MontantsSimulationModalContent;
